import { useMemo } from "react";
import { Gauge } from "lucide-react";
import { useDashboard } from "@/components/layout/AppShell";
import { ScoreRing, DimensionBar } from "@/components/ScoreRing";
import { PageHeader, EmptyState } from "@/components/PageState";
import { SourceBadge } from "@/components/SourceBadge";
import { InsightPanel } from "@/components/InsightPanel";
import { Skeleton } from "@/components/ui/skeleton";

export default function DigitalMaturity() {
  const { data, loading } = useDashboard();
  const weakest = useMemo(
    () => (data?.maturity_dimensions ? [...data.maturity_dimensions].sort((a, b) => a.score - b.score).slice(0, 2) : []),
    [data],
  );
  if (loading) return <Skeleton className="h-96 rounded-2xl" />;

  if (!data?.has_data) {
    return (
      <div>
        <PageHeader eyebrow="Diagnose" title="Digital Maturity" />
        <EmptyState icon={Gauge} title="Your Digital Maturity Score awaits your assessment"
          whatsMissing="ORBIT needs your assessment responses to calculate a maturity score."
          whyItMatters="Digital maturity shows how ready your processes, technology and data are to support growth." />
      </div>
    );
  }

  const s = data.scores.digital_maturity;

  return (
    <div>
      <PageHeader eyebrow="Diagnose" title="Digital Maturity"
        description="How ready your processes, technology and data are today — calculated deterministically from your responses.">
        <SourceBadge type="calculated" />
      </PageHeader>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="flex flex-col items-center justify-center rounded-2xl border border-border bg-card p-8 text-center">
          <ScoreRing score={s.score} level={s.level} size={168} stroke={12} />
          <p className="mt-4 font-display text-xl font-semibold">{s.level}</p>
          <p className="mt-1 text-sm text-muted-foreground">Digital Maturity Level</p>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 lg:col-span-2">
          <h3 className="mb-5 font-display text-base font-semibold">Dimension breakdown</h3>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {data.maturity_dimensions.map((d) => <DimensionBar key={d.key} label={d.label} score={d.score} />)}
          </div>
        </div>
      </div>

      <div className="mt-6">
        <InsightPanel operation="score" payload={{ score_key: "digital_maturity" }} heading="Why this score?" variant="insight" testid="insight-digital-maturity" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-6">
          <h3 className="mb-3 font-display text-base font-semibold text-emerald-700 dark:text-emerald-400">Strengths</h3>
          <ul className="space-y-2 text-sm">
            {data.strengths.map((t) => <li key={t} className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-600" />{t}</li>)}
          </ul>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6">
          <h3 className="mb-3 font-display text-base font-semibold text-red-700 dark:text-red-400">Transformation gaps</h3>
          <ul className="space-y-2 text-sm">
            {data.gaps.map((t) => <li key={t} className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-red-600" />{t}</li>)}
          </ul>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-border bg-card p-6">
        <div className="mb-4 flex items-center gap-2">
          <h3 className="font-display text-base font-semibold">What would move this score?</h3>
          <SourceBadge type="rule" />
        </div>
        <p className="mb-4 text-sm text-muted-foreground">Focused actions on your weakest dimensions would raise digital maturity the fastest.</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {weakest.map((d) => (
            <div key={d.key} className="rounded-xl border border-border bg-background p-4" data-testid={`move-score-${d.key}`}>
              <div className="flex items-baseline justify-between">
                <span className="text-sm font-semibold">{d.label}</span>
                <span className="font-num text-sm text-red-600">{d.score}</span>
              </div>
              <ol className="mt-3 space-y-1.5">
                {(d.improvements || []).map((t, i) => (
                  <li key={t} className="flex gap-2 text-sm text-muted-foreground"><span className="font-num text-xs text-accent">{i + 1}</span>{t}</li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-border bg-secondary/40 p-6">
        <div className="mb-2 flex items-center gap-2"><SourceBadge type="rule" /></div>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Your maturity score is a weighted combination of business process maturity, technology foundation, data
          readiness, operational visibility, customer channels and finance systems. Each dimension traces directly to
          your responses — nothing here is a black box, and AI never alters this deterministic calculation.
        </p>
      </div>
    </div>
  );
}
